import type { ReactNode } from 'react';
import { type FC, useState } from 'react';

import { ReactComponent as UploadIcon } from 'assets/img/upload.svg';
import classNames from 'classnames';

interface IDragUploadDropZoneProps {
  onUpload: (file: File, variant: 'upload' | 'drop') => void;
  formats: string;
  className?: string;
  title: ReactNode;
  titleMobile: ReactNode;
  description: ReactNode;
}

export const DragUploadButton: FC<IDragUploadDropZoneProps> = ({
  onUpload,
  formats,
  className,
  title,
  titleMobile,
  description
}) => {
  const [isDragging, setIsDragging] = useState(false);

  const validateFile = (file: File | FileList): boolean => {
    if (file instanceof FileList) {
      return file.length === 1 && validateFile(file[0]);
    }

    const fileExtension = file.name.split('.').pop();

    return formats.includes(`.${fileExtension}`);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || !validateFile(files)) return;

    onUpload(files[0], 'upload');
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = e.dataTransfer.files;

    if (!validateFile(files)) return;

    onUpload(files[0], 'drop');
  };

  return (
    <label
      aria-label='upload-button'
      className={classNames(
        `group w-full border-[#757575] bg-white transition-colors min-h-[96px] border-2 border-dashed rounded-2xl flex gap-x-2 justify-center items-center cursor-pointer`,
        'hover:border-[#5F30E2] hover:bg-[rgba(95,_48,_226,_0.08)]',
        { 'border-[#5F30E2] bg-[rgba(95,_48,_226,_0.08)]': isDragging },
        'max-sm:p-6 max-sm:min-h-auto max-sm:h-auto',
        className
      )}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <input
        type='file'
        className='hidden'
        onChange={handleFileUpload}
        accept={formats}
      />
      <span className='block space-y-2 font-bold'>
        <span
          className={classNames(
            'flex gap-x-1 text-base leading-5 justify-center text-[rgba(0,_0,_0,_0.87)] items-center group-hover:text-[#5F30E2]',
            { 'text-[#5F30E2]': isDragging },
            'max-sm:leading-[22px]'
          )}
        >
          <UploadIcon />
          <span className='max-sm:hidden'>{title}</span>
          <span className='hidden max-sm:block'>{titleMobile}</span>
        </span>
        <span
          className={classNames(
            'block text-[13px] leading-[18px] text-black/60 group-hover:text-[#42219E]',
            { 'text-[#42219E]': isDragging },
            'max-sm:text-center'
          )}
        >
          {description}
        </span>
      </span>
    </label>
  );
};
